import type {
  LevelAssessmentModel,
  ModelAxis,
  ModelSignalMatrix,
  ScopedSignalMatrix,
  SignalStrength,
} from "@shared/labs/level-assessment-model";
import {
  getSignalStatus,
  isSelectableForRouting,
  labelForSignal,
  SIGNAL_GROUP_TITLES,
  SIGNAL_LABELS,
  type SignalGroup,
} from "@shared/labs/level-assessment-signal-labels";
import { useEffect, useMemo, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { rpcClient } from "@/lib/rpc";
import { useActiveModel } from "../active-model-context";

const STRENGTH_RANK: Record<SignalStrength, number> = { strong: 0, moderate: 1, weak: 2 };

const STRENGTH_LABELS: Record<SignalStrength, string> = {
  strong: "Forte",
  moderate: "Moderado",
  weak: "Fraco",
};

const MEASUREMENT_TAG: Record<string, { text: string; color: string }> = {
  keyword: { text: "regex", color: "var(--r-ink-muted)" },
  numeric: { text: "cálculo", color: "var(--r-ink-muted)" },
  llm: { text: "LLM", color: "var(--r-forest, #1d9e75)" },
};

const GROUP_ORDER: SignalGroup[] = [
  "founder",
  "progression",
  "management",
  "stakeholder",
  "product-systems",
  "business",
  "ic-depth",
  "hr",
  "impact",
  "environment",
  "geography",
  "academic",
  "recognition",
  "credentials",
  "priors",
  "flags",
];

function lsKeyForOverrides(modelId: string): string {
  return `labs.level-assessment.signal-routing-overrides.${modelId}`;
}

/** Override value for one (signal, axis) pair. `"none"` explicitly removes a
 *  default routing; a `SignalStrength` overrides the default. */
type OverrideValue = SignalStrength | "none";
type OverrideMap = Record<string, Record<string, OverrideValue>>;

function defaultStrength(
  matrix: ScopedSignalMatrix,
  signal: string,
  axisId: string,
): SignalStrength | null {
  return (matrix.signals[signal]?.axes?.[axisId] as SignalStrength | undefined) ?? null;
}

function effectiveStrength(
  matrix: ScopedSignalMatrix,
  signal: string,
  axisId: string,
  overrides: OverrideMap,
): SignalStrength | null {
  const ov = overrides[signal]?.[axisId];
  if (ov === "none") return null;
  if (ov) return ov;
  return defaultStrength(matrix, signal, axisId);
}

function nextInCycle(current: SignalStrength | null): OverrideValue {
  if (current === null) return "weak";
  if (current === "weak") return "moderate";
  if (current === "moderate") return "strong";
  return "none";
}

function applyCellChange(
  matrix: ScopedSignalMatrix,
  overrides: OverrideMap,
  signal: string,
  axisId: string,
  next: OverrideValue,
): OverrideMap {
  const def = defaultStrength(matrix, signal, axisId);
  const matchesDefault = (next === "none" && def === null) || (next !== "none" && next === def);

  const out: OverrideMap = { ...overrides };
  if (matchesDefault) {
    const cur = { ...(out[signal] ?? {}) };
    delete cur[axisId];
    if (Object.keys(cur).length === 0) delete out[signal];
    else out[signal] = cur;
  } else {
    out[signal] = { ...(out[signal] ?? {}), [axisId]: next };
  }
  return out;
}

function countOverrides(overrides: OverrideMap): number {
  let n = 0;
  for (const entry of Object.values(overrides)) {
    n += Object.keys(entry).length;
  }
  return n;
}

/** Remove the overrides belonging to one scope (by its axis ids), keeping any
 *  unsaved edits for other scopes. Used after persisting a scope so the local
 *  preview state only reflects what's still pending. */
function stripScopeOverrides(overrides: OverrideMap, scopeAxisIds: Set<string>): OverrideMap {
  const out: OverrideMap = {};
  for (const [signal, byAxis] of Object.entries(overrides)) {
    const kept = Object.fromEntries(
      Object.entries(byAxis).filter(([axisId]) => !scopeAxisIds.has(axisId)),
    );
    if (Object.keys(kept).length > 0) out[signal] = kept;
  }
  return out;
}

/** Build a JSON snippet of every modified signal — full axes object
 *  (defaults + overrides merged) so the user can paste back into the
 *  model's `signal-matrix.json` to promote an experiment to the default. */
function buildExportSnippet(
  matrix: ScopedSignalMatrix,
  axes: ModelAxis[],
  overrides: OverrideMap,
): string {
  const signals = Object.keys(overrides);
  if (signals.length === 0) return "// (sem alterações)\n";
  const lines: string[] = [
    "// Edições locais — cole em docs/labs/models/<id>/signal-matrix.json pra promover ao default.\n",
  ];
  for (const signal of signals.sort()) {
    const merged: Record<string, SignalStrength> = {};
    for (const axis of axes) {
      const eff = effectiveStrength(matrix, signal, axis.id, overrides);
      if (eff !== null) merged[axis.id] = eff;
    }
    const axesStr = Object.entries(merged)
      .map(([axisId, strength]) => `"${axisId}": "${strength}"`)
      .join(", ");
    if (axesStr.length === 0) {
      lines.push(`"${signal}": { /* sem eixos — sinal desligado */ },`);
    } else {
      lines.push(`"${signal}": { "axes": { ${axesStr} } },`);
    }
  }
  return lines.join("\n") + "\n";
}

function StrengthCell({
  strength,
  overridden,
  onClick,
}: {
  strength: SignalStrength | null;
  overridden: boolean;
  onClick: () => void;
}) {
  const label = strength === null ? "—" : STRENGTH_LABELS[strength];
  const title =
    strength === null
      ? "Clique pra adicionar (fraca → moderada → forte → nenhuma)"
      : `Clique pra promover (${STRENGTH_LABELS[strength]} → próxima)`;

  const chipStyle: React.CSSProperties =
    strength === null
      ? {
          background: "transparent",
          color: "var(--r-ink-faint, #c7c2bb)",
          border: "1px dashed transparent",
        }
      : strength === "strong"
        ? {
            background: "var(--r-ink)",
            color: "var(--r-bg, #fffaf3)",
            border: "1px solid var(--r-ink)",
          }
        : strength === "moderate"
          ? { background: "transparent", color: "var(--r-ink)", border: "1px solid var(--r-ink)" }
          : {
              background: "transparent",
              color: "var(--r-ink-muted)",
              border: "1px dashed var(--r-ink-faint, #c7c2bb)",
            };

  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={`${label} — ${title}`}
      style={{
        position: "relative",
        background: "transparent",
        border: "none",
        padding: 4,
        cursor: "pointer",
        borderRadius: 999,
      }}
    >
      <span
        style={{
          display: "inline-block",
          padding: "2px 10px",
          borderRadius: 999,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.02em",
          minWidth: 62,
          ...chipStyle,
        }}
      >
        {label}
      </span>
      {overridden && (
        <span
          aria-hidden
          style={{
            position: "absolute",
            top: 2,
            right: 2,
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: "var(--r-amber, #b07811)",
          }}
        />
      )}
    </button>
  );
}

export function SignalMatrix() {
  const { toast } = useToast();
  const { modelId } = useActiveModel();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [model, setModel] = useState<LevelAssessmentModel | null>(null);
  const [matrix, setMatrix] = useState<ModelSignalMatrix | null>(null);
  const [overrides, setOverrides] = useState<OverrideMap>({});
  const [scope, setScope] = useState<string>("cross");
  const [exportOpen, setExportOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  // The scope being edited: "cross" or a discipline id. Its routing + axes.
  const scopedMatrix: ScopedSignalMatrix | null = useMemo(() => {
    if (!matrix) return null;
    return scope === "cross" ? matrix.cross : (matrix.disciplines[scope] ?? { signals: {} });
  }, [matrix, scope]);
  const scopeAxes: ModelAxis[] = useMemo(() => {
    if (!model) return [];
    return scope === "cross"
      ? model.crossAxes
      : (model.disciplines.find((d) => d.id === scope)?.axes ?? []);
  }, [model, scope]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setModel(null);
    setMatrix(null);
    setOverrides({});
    setLoadError(null);
    (async () => {
      try {
        const res = (await rpcClient.labs.levelAssessmentModelGet({ modelId })) as {
          model: LevelAssessmentModel;
          matrix: ModelSignalMatrix;
        };
        if (cancelled) return;
        setModel(res.model);
        setMatrix(res.matrix);
        try {
          const raw = localStorage.getItem(lsKeyForOverrides(modelId));
          if (raw) setOverrides(JSON.parse(raw));
        } catch {
          /* ignore corrupt storage */
        }
      } catch (e) {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : "Erro inesperado");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modelId]);

  // Persist overrides to localStorage per model.
  useEffect(() => {
    const key = lsKeyForOverrides(modelId);
    if (Object.keys(overrides).length === 0) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(overrides));
  }, [overrides, modelId]);

  const overrideCount = countOverrides(overrides);

  function cycleCell(signal: string, axisId: string) {
    if (!scopedMatrix) return;
    const cur = effectiveStrength(scopedMatrix, signal, axisId, overrides);
    const next = nextInCycle(cur);
    setOverrides((prev) => applyCellChange(scopedMatrix, prev, signal, axisId, next));
  }

  function resetAll() {
    setOverrides({});
    toast({
      title: "Edições descartadas",
      description: "A matriz voltou ao roteamento padrão do modelo.",
    });
  }

  async function saveMatrix() {
    if (saving || !model || overrideCount === 0) return;
    setSaving(true);
    try {
      const res = (await rpcClient.labs.levelAssessmentSignalMatrixSave({
        modelId,
        scope,
        overrides,
      })) as { matrix: ModelSignalMatrix };
      setMatrix(res.matrix);
      // Persisted this scope — drop its overrides but keep any unsaved edits
      // for other scopes (the override map is model-level).
      const scopeAxisIds = new Set(scopeAxes.map((a) => a.id));
      setOverrides((prev) => stripScopeOverrides(prev, scopeAxisIds));
      toast({
        title: "Matriz salva",
        description: `Roteamento do escopo ${scope} persistido no modelo. Regenere os prompts pra refletir a mudança.`,
      });
    } catch (e) {
      toast({
        title: "Falha ao salvar matriz",
        description: e instanceof Error ? e.message : "Erro inesperado",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  }

  async function regeneratePrompts() {
    if (regenerating || !model) return;
    // The matrix is the single source of truth for both the prompts and the
    // runtime slice. Regenerating from unsaved local edits would persist
    // prompts that describe a routing the saved matrix doesn't have — the
    // exact drift this flow is meant to avoid. Require a saved matrix first.
    if (overrideCount > 0) {
      toast({
        title: "Salve a matriz primeiro",
        description:
          'Há edições não salvas. Clique em "Salvar matriz" antes de regenerar — os prompts são gerados a partir do roteamento persistido.',
        variant: "destructive",
      });
      return;
    }
    const proceed = confirm(
      `Vou pedir ao Opus pra reescrever os ${scopeAxes.length} prompts de eixo (escopo ${scope}) a partir da matriz salva. Continuar?`,
    );
    if (!proceed) return;
    setRegenerating(true);
    try {
      const res = (await rpcClient.labs.levelAssessmentAxisPromptsRegenerate({
        modelId: modelId,
        scope,
      })) as {
        prompts: Record<string, string>;
        errors: Record<string, string>;
      };
      const okAxes = Object.keys(res.prompts ?? {});
      const errAxes = Object.keys(res.errors ?? {});
      if (okAxes.length === 0) {
        toast({
          title: "Regeneração falhou",
          description: `Nenhum eixo regenerado. Erros: ${errAxes.join(", ") || "desconhecido"}`,
          variant: "destructive",
        });
      } else if (errAxes.length > 0) {
        toast({
          title: `${okAxes.length}/${scopeAxes.length} eixos regenerados`,
          description: `Falhou em: ${errAxes.join(", ")}. Os demais já foram aplicados ao modelo.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: `${okAxes.length} prompts regenerados`,
          description: "Aplicados ao modelo — abra a aba Prompts pra revisar.",
        });
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : "Erro inesperado";
      toast({
        title: "Falha ao regenerar prompts",
        description: message,
        variant: "destructive",
      });
    } finally {
      setRegenerating(false);
    }
  }

  async function copyExport() {
    if (!scopedMatrix || !model) return;
    const snippet = buildExportSnippet(scopedMatrix, scopeAxes, overrides);
    try {
      await navigator.clipboard.writeText(snippet);
      toast({
        title: "Copiado",
        description: "Snippet JSON com suas edições copiado pra área de transferência.",
      });
    } catch {
      toast({
        title: "Copie manualmente",
        description: "Não consegui acessar a área de transferência — selecione o texto e copie.",
        variant: "destructive",
      });
    }
  }

  // Build grouped axis signals: the persisted matrix's routed signals PLUS
  // any catalog signal (SIGNAL_LABELS) the matrix doesn't know yet — newly
  // shipped extractors must be visible and routable here without requiring a
  // full re-bootstrap. Catalog-only rows render unrouted ("—" on every axis);
  // routing one creates the matrix entry on "Salvar matriz".
  //
  // Management gate: a `deprecated` signal the matrix doesn't already route is
  // NOT offered as a new catalog row. One already routed in the persisted matrix
  // still appears (flagged below) so an author can see it and remove it.
  const grouped = useMemo(() => {
    if (!scopedMatrix) return new Map<SignalGroup, string[]>();
    const axisSignals = Object.keys(scopedMatrix.signals).filter(
      (k) => scopedMatrix.signals[k].axes,
    );
    for (const key of Object.keys(SIGNAL_LABELS)) {
      if (key in scopedMatrix.signals) continue;
      if (!isSelectableForRouting(key)) continue;
      axisSignals.push(key);
    }
    const groups = new Map<SignalGroup, string[]>();
    for (const key of axisSignals) {
      const g = labelForSignal(key).group;
      const arr = groups.get(g) ?? [];
      arr.push(key);
      groups.set(g, arr);
    }
    for (const arr of Array.from(groups.values())) {
      arr.sort((a: string, b: string) => {
        const minA = Math.min(
          ...scopeAxes.map((ax) => {
            const s = defaultStrength(scopedMatrix, a, ax.id);
            return s ? STRENGTH_RANK[s] : 99;
          }),
        );
        const minB = Math.min(
          ...scopeAxes.map((ax) => {
            const s = defaultStrength(scopedMatrix, b, ax.id);
            return s ? STRENGTH_RANK[s] : 99;
          }),
        );
        return minA - minB || a.localeCompare(b);
      });
    }
    return groups;
  }, [scopedMatrix, scopeAxes]);

  const priorKeys = useMemo(() => {
    if (!scopedMatrix) return [];
    return Object.entries(scopedMatrix.signals)
      .filter(([, r]) => r.prior)
      .map(([k]) => k);
  }, [scopedMatrix]);

  if (loading) {
    return (
      <div style={{ padding: 24, color: "var(--r-ink-muted)", fontSize: 13 }}>
        Carregando matriz de sinais…
      </div>
    );
  }
  if (loadError || !model || !matrix || !scopedMatrix) {
    return (
      <div style={{ padding: 24, color: "var(--r-coral, #f3354b)", fontSize: 13 }}>
        Falha ao carregar a matriz: {loadError ?? "modelo não disponível"}
      </div>
    );
  }

  return (
    <div data-testid="signal-matrix">
      {/* Header / actions strip */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16,
          marginBottom: 20,
          flexWrap: "wrap",
        }}
      >
        <div style={{ fontSize: 12, color: "var(--r-ink-muted)", lineHeight: 1.5, maxWidth: 640 }}>
          Cada linha é um extrator. As colunas são os {scopeAxes.length} eixos ({scope}) do modelo{" "}
          <strong>{model.name}</strong>.{" "}
          <strong style={{ color: "var(--r-ink)" }}>Clique numa célula</strong> pra alternar a força
          (nenhuma → fraca → moderada → forte → nenhuma). Um sinal pode aparecer em vários eixos com
          forças diferentes. Edições ficam{" "}
          <strong style={{ color: "var(--r-amber, #b07811)" }}>pendentes</strong> até você clicar em{" "}
          <strong style={{ color: "var(--r-ink)" }}>Salvar matriz</strong>, que persiste o
          roteamento no modelo — passando a valer para a avaliação e para a regeneração de prompts.
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
          {model.disciplines.length > 0 && (
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              data-testid="signal-matrix-scope"
              style={{
                fontSize: 12,
                fontWeight: 600,
                padding: "6px 10px",
                borderRadius: 6,
                border: "1px solid var(--r-ink-faint, #e9e3d8)",
                background: "var(--r-bg, #fffaf3)",
                color: "var(--r-ink)",
              }}
            >
              <option value="cross">Cross</option>
              {model.disciplines.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.label}
                </option>
              ))}
            </select>
          )}
          <span
            style={{
              fontSize: 12,
              color: overrideCount > 0 ? "var(--r-amber, #b07811)" : "var(--r-ink-muted)",
              fontWeight: 600,
            }}
            data-testid="signal-matrix-override-count"
          >
            {overrideCount === 0
              ? "Sem edições"
              : overrideCount === 1
                ? "1 célula editada"
                : `${overrideCount} células editadas`}
          </span>
          <button
            type="button"
            className="btn primary"
            onClick={saveMatrix}
            disabled={saving || overrideCount === 0}
            data-testid="signal-matrix-save"
            title="Persiste as edições de roteamento deste escopo no modelo (passa a valer na avaliação e na regeneração de prompts)"
          >
            {saving ? "Salvando…" : "Salvar matriz"}
          </button>
          <button
            type="button"
            className="btn ghost"
            onClick={regeneratePrompts}
            disabled={regenerating}
            data-testid="signal-matrix-regenerate"
            title={`Pede ao Opus pra reescrever os ${scopeAxes.length} prompts de eixo (${scope}) a partir da matriz salva`}
          >
            {regenerating ? "Regenerando…" : "Regenerar prompts (Opus)"}
          </button>
          <button
            type="button"
            className="btn ghost"
            onClick={() => setExportOpen((v) => !v)}
            disabled={overrideCount === 0}
            data-testid="signal-matrix-export"
          >
            {exportOpen ? "Fechar export" : "Exportar"}
          </button>
          <button
            type="button"
            className="btn ghost"
            onClick={resetAll}
            disabled={overrideCount === 0}
            data-testid="signal-matrix-reset"
          >
            Descartar edições
          </button>
        </div>
      </div>

      {/* Export panel (JSON snippet) */}
      {exportOpen && (
        <div
          className="card"
          style={{ padding: 16, marginBottom: 20, background: "var(--r-bg-soft, #fff8ec)" }}
        >
          <div style={{ fontSize: 11, color: "var(--r-ink-muted)", marginBottom: 8 }}>
            Snippet JSON com as entradas modificadas (eixos mergeados). Cole em{" "}
            <code>docs/labs/models/{modelId}/signal-matrix.json</code> pra promover ao default.
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <button type="button" className="btn primary" onClick={copyExport}>
              Copiar
            </button>
          </div>
          <textarea
            readOnly
            value={buildExportSnippet(scopedMatrix, scopeAxes, overrides)}
            rows={Math.min(20, Math.max(4, Object.keys(overrides).length + 2))}
            spellCheck={false}
            style={{
              width: "100%",
              fontFamily: "ui-monospace, SFMono-Regular, monospace",
              fontSize: 12,
              lineHeight: 1.5,
              padding: 12,
              border: "1px solid var(--r-ink-faint, #e9e3d8)",
              borderRadius: 6,
              background: "var(--r-bg, #fffaf3)",
              color: "var(--r-ink)",
              resize: "vertical",
            }}
          />
        </div>
      )}

      {GROUP_ORDER.filter((g) => g !== "priors" && grouped.has(g)).map((group) => {
        const keys = grouped.get(group) ?? [];
        return (
          <div key={group} style={{ marginBottom: 24 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "var(--r-ink-muted)",
                textTransform: "uppercase",
                letterSpacing: "0",
                marginBottom: 8,
              }}
            >
              {SIGNAL_GROUP_TITLES[group]}
            </div>
            <div className="card" style={{ padding: 0, overflow: "hidden" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 13,
                  color: "var(--r-ink)",
                }}
              >
                <thead>
                  <tr style={{ borderBottom: "1px solid var(--r-ink-faint, #e9e3d8)" }}>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "10px 16px",
                        fontWeight: 600,
                        fontSize: 11,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        color: "var(--r-ink-muted)",
                        width: "44%",
                      }}
                    >
                      Sinal extraído
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "10px 8px",
                        fontWeight: 600,
                        fontSize: 11,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        color: "var(--r-ink-muted)",
                        width: "8%",
                      }}
                    >
                      Tipo
                    </th>
                    {scopeAxes.map((axis) => (
                      <th
                        key={axis.id}
                        style={{
                          textAlign: "center",
                          padding: "10px 8px",
                          fontWeight: 600,
                          fontSize: 11,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                          color: "var(--r-ink-muted)",
                        }}
                      >
                        {axis.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {keys.map((key, i) => {
                    const meta = labelForSignal(key);
                    const status = getSignalStatus(key);
                    return (
                      <tr
                        key={key}
                        style={{
                          borderBottom:
                            i === keys.length - 1
                              ? "none"
                              : "1px solid var(--r-ink-faint, #f0ebe1)",
                          opacity: status === "deprecated" ? 0.6 : 1,
                        }}
                      >
                        <td style={{ padding: "12px 16px", verticalAlign: "top" }}>
                          <div style={{ fontWeight: 500, color: "var(--r-ink)", lineHeight: 1.35 }}>
                            {meta.label}
                            {status !== "active" && (
                              <span
                                title={
                                  status === "deprecated"
                                    ? "Sinal obsoleto — não oferecido para novo roteamento. Já roteado neste modelo; remova a aresta para descontinuá-lo. A extração continua e modelos existentes não mudam."
                                    : "Sinal experimental — roteável manualmente, mas não incluído automaticamente no bootstrap."
                                }
                                style={{
                                  marginLeft: 8,
                                  fontSize: 9,
                                  fontWeight: 700,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.05em",
                                  padding: "1px 6px",
                                  borderRadius: 4,
                                  verticalAlign: "middle",
                                  color:
                                    status === "deprecated"
                                      ? "var(--r-ink-muted)"
                                      : "var(--r-amber, #b07811)",
                                  background:
                                    status === "deprecated"
                                      ? "var(--r-ink-faint, #f0ebe1)"
                                      : "var(--r-amber-bg, #fbf1dd)",
                                }}
                              >
                                {status === "deprecated" ? "obsoleto" : "experimental"}
                              </span>
                            )}
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              color: "var(--r-ink-muted)",
                              marginTop: 2,
                              lineHeight: 1.45,
                            }}
                          >
                            {meta.description}
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              color: "var(--r-ink-faint, #a8a298)",
                              marginTop: 4,
                              fontFamily: "ui-monospace, SFMono-Regular, monospace",
                            }}
                          >
                            {key}
                          </div>
                        </td>
                        <td style={{ padding: "12px 8px", verticalAlign: "top" }}>
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                              color: MEASUREMENT_TAG[meta.measurement].color,
                            }}
                          >
                            {MEASUREMENT_TAG[meta.measurement].text}
                          </span>
                        </td>
                        {scopeAxes.map((axis) => {
                          const eff = effectiveStrength(scopedMatrix, key, axis.id, overrides);
                          const def = defaultStrength(scopedMatrix, key, axis.id);
                          const overridden = eff !== def;
                          return (
                            <td
                              key={axis.id}
                              style={{
                                padding: "10px 4px",
                                verticalAlign: "top",
                                textAlign: "center",
                              }}
                            >
                              <StrengthCell
                                strength={eff}
                                overridden={overridden}
                                onClick={() => cycleCell(key, axis.id)}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {/* Priors panel — read-only (priors belong to no axis) */}
      <div style={{ marginTop: 32 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "var(--r-ink-muted)",
            textTransform: "uppercase",
            letterSpacing: "0",
            marginBottom: 8,
          }}
        >
          {SIGNAL_GROUP_TITLES.priors}
        </div>
        <div
          className="card"
          style={{
            padding: "16px 20px",
            fontSize: 13,
            color: "var(--r-ink-muted)",
            lineHeight: 1.55,
          }}
        >
          Sinais passados a{" "}
          <strong style={{ color: "var(--r-ink)" }}>todos os eixos como contexto</strong> (não
          pertencem a um eixo específico — não editáveis aqui). Limitam o teto de senioridade.
          <ul style={{ margin: "10px 0 0 18px", padding: 0, color: "var(--r-ink)" }}>
            {priorKeys.map((key) => {
              const meta = labelForSignal(key);
              return (
                <li key={key} style={{ marginBottom: 4 }}>
                  <strong>{meta.label}</strong>{" "}
                  <span style={{ color: "var(--r-ink-muted)", fontSize: 12 }}>
                    — {meta.description}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
